import { IconButton, InputAdornment, TextField, useTheme } from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { focusRingSx, INNER_RADIUS, tapSquareSx } from "../utils/tokens";

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** 見えるラベルは置かず、placeholder と aria-label に同じ文言を入れる。 */
  label: string;
  /** Enter で検索する。 */
  onEnter?: () => void;
  /** 入力があるときだけ出す×ボタンを押したとき。 */
  onClear: () => void;
  maxLength?: number;
  id?: string;
}

/**
 * ツールバーの検索欄。高さ PC 40px／SP 44px、左に検索アイコン、入力があるときだけ右に消去の×ボタン。
 * 枠は border.main（副ボタンの枠と色をそろえる）、フォーカスで primary.main。
 */
export const ReassistSearchField: React.FC<Props> = ({
  value,
  onChange,
  label,
  onEnter,
  onClear,
  maxLength = 200,
  id,
}) => {
  const theme = useTheme();
  return (
    <TextField
      id={id}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") onEnter?.();
      }}
      placeholder={label}
      size="small"
      fullWidth
      inputProps={{ "aria-label": label, maxLength }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchOutlined
              aria-hidden
              sx={{ fontSize: 20, color: "text.secondary" }}
            />
          </InputAdornment>
        ),
        endAdornment:
          value !== "" ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="検索語を消去"
                onClick={onClear}
                edge="end"
                sx={{
                  ...tapSquareSx(28),
                  width: 28,
                  height: 28,
                  ...focusRingSx(theme),
                }}
              >
                <CloseOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        sx: {
          height: { xs: 44, [MOBILE_BREAKPOINT]: 40 },
          backgroundColor: theme.palette.white.main,
          borderRadius: INNER_RADIUS,
          // iOS Safari は 16px 未満だとフォーカス時にズームするため、SP の入力値は 16px
          "& .MuiInputBase-input": {
            fontSize: { xs: 16, [MOBILE_BREAKPOINT]: 14 },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.border.main,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
        },
      }}
    />
  );
};
